import { useState, useEffect, useMemo } from 'react'
import { classes, reports, standards as standardsApi } from '@/api/client'
import type { ClassInfo } from '@/api/client'
import { useSort, sortRows, SortTh, type SortValue } from './sortable'

const MASTERY_COLORS: Record<string, string> = {
  mastered:     '#10b981',
  proficient:   '#6366f1',
  developing:   '#f59e0b',
  attempted:    '#6b7280',
  not_started:  '#1e1b4b',
}

const MASTERY_LABELS: Record<string, string> = {
  mastered:    '⭐',
  proficient:  '✅',
  developing:  '📈',
  attempted:   '🔄',
  not_started: '—',
}

const MASTERY_TEXT: Record<string, string> = {
  mastered:    'Mastered',
  proficient:  'Proficient',
  developing:  'Developing',
  attempted:   'Attempted',
  not_started: 'Not started',
}

const ON_TRACK = ['mastered', 'proficient']

// Rank for sorting a standard column: higher = further along.
const MASTERY_RANK: Record<string, number> = {
  mastered: 4, proficient: 3, developing: 2, attempted: 1, not_started: 0,
}

// 'name' | 'mastered' | 'proficient' | `std:<standard_id>`
type SortKey = string
type GrowthSortKey = 'standard' | 'pre' | 'post' | 'change'

interface Props { mode: 'mastery' | 'growth' }

export default function MasteryReport({ mode }: Props) {
  const [classList, setClassList]   = useState<(ClassInfo & { class_code: string; student_count: number })[]>([])
  const [classId, setClassId]       = useState<number | null>(null)
  const [session, setSession]       = useState('')
  const [preSession, setPreSession] = useState('pre_2026')
  const [postSession, setPostSession] = useState('post_2026')
  const [reportData, setReportData] = useState<any>(null)
  const [growthData, setGrowthData] = useState<any>(null)
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState('')

  // M-STEP averages by standard_id (not present in the report payload)
  const [mstepById, setMstepById]   = useState<Record<string, number | null>>({})

  // Grid controls + drill-down
  const gridSort                    = useSort<SortKey>('name', 'asc')
  const growthSort                  = useSort<GrowthSortKey>(null)
  const [strandFilter, setStrandFilter] = useState('')
  const [selectedStudent, setSelectedStudent] = useState<any>(null)

  useEffect(() => {
    classes.list()
      .then(list => {
        setClassList(list as any)
        if (list.length > 0) setClassId(list[0].id)
      })
      .catch(err => setError(err.message || 'Failed to load classes'))
    standardsApi.list()
      .then(list => {
        const map: Record<string, number | null> = {}
        list.forEach(s => { map[s.standard_id] = s.mstep_avg_pct })
        setMstepById(map)
      })
      .catch(() => { /* M-STEP overlay is optional */ })
  }, [])

  async function loadReport() {
    if (!classId) return
    setLoading(true)
    setError('')
    try {
      if (mode === 'mastery') {
        setReportData(await reports.mastery(classId, session || undefined))
      } else {
        setGrowthData(await reports.growth(classId, preSession, postSession))
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate report')
    } finally {
      setLoading(false)
    }
  }

  async function downloadCSV() {
    if (!classId) return
    try {
      const csv = await reports.csv(classId, session || undefined)
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `mastery_report_${classId}_${session || 'all'}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message || 'Failed to download CSV')
    }
  }

  // ── Derived analytics ─────────────────────────────────────────────────────
  const stds: any[]  = reportData?.standards ?? []
  const studs: any[] = reportData?.students ?? []

  // Per-standard: how many students are on-track (proficient or better),
  // and how many have started it at all (any status beyond not_started).
  const standardStats = useMemo(() => {
    return stds.map(s => {
      const onTrack = studs.filter(stu => ON_TRACK.includes(stu.standards[s.standard_id]?.mastery)).length
      const started = studs.filter(stu => {
        const m = stu.standards[s.standard_id]?.mastery
        return m && m !== 'not_started'
      }).length
      const rate = studs.length ? onTrack / studs.length : 0
      return { ...s, onTrack, started, rate, total: studs.length, mstep: mstepById[s.standard_id] ?? null }
    })
  }, [reportData, mstepById])

  // Class at a glance: roll standardStats up by strand
  const strandSummary = useMemo(() => {
    const byStrand: Record<string, { count: number; rateSum: number; onTrack: number; started: number; total: number }> = {}
    standardStats.forEach(s => {
      const k = s.strand || 'Other'
      if (!byStrand[k]) byStrand[k] = { count: 0, rateSum: 0, onTrack: 0, started: 0, total: studs.length }
      byStrand[k].count++
      byStrand[k].rateSum += s.rate
      byStrand[k].onTrack += s.onTrack
      byStrand[k].started += s.started
    })
    return Object.entries(byStrand)
      .map(([strand, v]) => ({
        strand, count: v.count, avgRate: v.rateSum / v.count,
        onTrack: v.onTrack, total: v.count * v.total, started: v.started,
      }))
      .sort((a, b) => a.avgRate - b.avgRate)
  }, [standardStats])

  const strandOptions = useMemo(
    () => Array.from(new Set(stds.map(s => s.strand).filter(Boolean))).sort(),
    [reportData],
  )

  // Focus here: standards most in need of attention.
  // Rank by need = how far the class is from on-track, nudged up when the
  // standard has historically been hard on M-STEP.
  const focusStandards = useMemo(() => {
    return standardStats
      .map(s => {
        const mstepGap = s.mstep != null ? (1 - s.mstep / 100) : 0
        const need = (1 - s.rate) * 0.7 + mstepGap * 0.3
        return { ...s, need }
      })
      // Only surface standards the class has actually begun, so a brand-new
      // class doesn't light up every standard as a "problem".
      .filter(s => s.started > 0 && s.rate < 0.8)
      .sort((a, b) => b.need - a.need)
      .slice(0, 4)
  }, [standardStats])

  // Visible standard columns honoring the strand filter
  const visibleStds = useMemo(
    () => (strandFilter ? stds.filter(s => s.strand === strandFilter) : stds),
    [reportData, strandFilter],
  )

  // Per-student counts (over visible standards) + sorting
  const rows = useMemo(() => {
    const withCounts = studs.map(stu => {
      const mastered = visibleStds.filter(s => stu.standards[s.standard_id]?.mastery === 'mastered').length
      const proficient = visibleStds.filter(s => ON_TRACK.includes(stu.standards[s.standard_id]?.mastery)).length
      return { stu, mastered, proficient }
    })
    const key = gridSort.key
    let get: ((r: typeof withCounts[number]) => SortValue) | null = null
    if (key === 'name') get = r => r.stu.display_name
    else if (key === 'mastered') get = r => r.mastered
    else if (key === 'proficient') get = r => r.proficient
    else if (key?.startsWith('std:')) {
      const id = key.slice(4)
      // Level first, then score within the level (e.g. two "developing" kids at 55% vs 68%).
      get = r => {
        const m = r.stu.standards[id]
        return (MASTERY_RANK[m?.mastery || 'not_started'] ?? 0) * 1000 + (m?.pct ?? 0)
      }
    }
    return sortRows(withCounts, get, gridSort.dir, r => r.stu.display_name)
  }, [reportData, visibleStds, gridSort.key, gridSort.dir])

  // If the strand filter hides the standard being sorted on, fall back to name.
  useEffect(() => {
    if (gridSort.key?.startsWith('std:') && !visibleStds.some(s => `std:${s.standard_id}` === gridSort.key)) {
      gridSort.toggle('name', 'asc')
    }
  }, [visibleStds])

  // Growth rows: server order until a header is clicked.
  const growthRows = useMemo(() => {
    const g: any[] = growthData?.growth ?? []
    const get: Record<GrowthSortKey, (r: any) => SortValue> = {
      standard: r => r.standard_id,
      pre:      r => r.pre?.avg_pct,
      post:     r => r.post?.avg_pct,
      change:   r => r.delta,
    }
    return sortRows(g, growthSort.key ? get[growthSort.key] : null, growthSort.dir, r => r.standard_id)
  }, [growthData, growthSort.key, growthSort.dir])

  const fieldStyle = {
    background: '#1e1b4b', border: '1px solid #4338ca', color: '#e0e7ff',
    fontFamily: 'system-ui', fontSize: '13px', borderRadius: 4,
    padding: '6px 10px', outline: 'none',
  }
  const pressFont = '"Press Start 2P", monospace'

  return (
    <div className="max-w-6xl mx-auto">
      <h2 style={{ fontFamily: pressFont, fontSize: '12px', color: '#a78bfa', marginBottom: 20 }}>
        {mode === 'mastery' ? '📊 Standards Mastery Report' : '📈 Pre/Post Growth Report'}
      </h2>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 items-end mb-6 p-4 rounded-lg"
           style={{ background: '#0f0f23', border: '1px solid #1e1b4b' }}>
        <div>
          <label className="block mb-1" style={{ fontFamily: pressFont, fontSize: '7px', color: '#818cf8' }}>CLASS</label>
          <select value={classId || ''} onChange={e => setClassId(Number(e.target.value))} style={fieldStyle}>
            {classList.map(c => <option key={c.id} value={c.id}>{c.name} (Grade {c.grade})</option>)}
          </select>
        </div>

        {mode === 'mastery' && (
          <div>
            <label className="block mb-1" style={{ fontFamily: pressFont, fontSize: '7px', color: '#818cf8' }}>SESSION LABEL</label>
            <input value={session} onChange={e => setSession(e.target.value)}
              placeholder="pre_2026 (or leave blank)" style={{ ...fieldStyle, minWidth: 180 }} />
          </div>
        )}

        {mode === 'growth' && (
          <>
            <div>
              <label className="block mb-1" style={{ fontFamily: pressFont, fontSize: '7px', color: '#818cf8' }}>PRE SESSION</label>
              <input value={preSession} onChange={e => setPreSession(e.target.value)} style={{ ...fieldStyle, minWidth: 140 }} />
            </div>
            <div>
              <label className="block mb-1" style={{ fontFamily: pressFont, fontSize: '7px', color: '#818cf8' }}>POST SESSION</label>
              <input value={postSession} onChange={e => setPostSession(e.target.value)} style={{ ...fieldStyle, minWidth: 140 }} />
            </div>
          </>
        )}

        <button onClick={loadReport} disabled={!classId || loading}
          style={{ fontFamily: pressFont, fontSize: '8px',
                   background: '#4f46e5', color: '#ffffff', border: '2px solid #818cf8',
                   padding: '8px 16px', borderRadius: 4, cursor: 'pointer', opacity: (!classId || loading) ? 0.5 : 1 }}>
          {loading ? 'LOADING...' : 'GENERATE'}
        </button>

        {mode === 'mastery' && reportData && (
          <button onClick={downloadCSV}
            style={{ fontFamily: pressFont, fontSize: '8px',
                     background: '#065f46', color: '#34d399', border: '2px solid #34d399',
                     padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>
            ↓ DOWNLOAD CSV
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-3 rounded-lg" style={{ background: '#3f1d1d', border: '1px solid #ef4444' }}>
          <span style={{ fontFamily: pressFont, fontSize: '8px', color: '#fca5a5' }}>⚠ {error}</span>
        </div>
      )}

      {/* ── MASTERY MODE ──────────────────────────────────────────────────── */}
      {mode === 'mastery' && reportData && studs.length > 0 && (
        <div>
          {/* Class at a glance */}
          <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#6b7280', marginBottom: 10 }}>
            Share of students who are proficient or better in each strand. These update live as
            students play — a strand stays gray until someone attempts it, so early in a unit low
            numbers are expected, not alarming.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {strandSummary.map(s => {
              const pct = Math.round(s.avgRate * 100)
              const notStarted = s.started === 0
              const tone = notStarted ? '#6b7280' : pct >= 70 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#ef4444'
              return (
                <div key={s.strand} className="p-3 rounded-lg"
                     style={{ background: '#0f0f23', border: `1px solid ${notStarted ? '#1e1b4b' : tone + '55'}` }}>
                  <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#94a3b8', marginBottom: 6, minHeight: 30 }}>
                    {s.strand}
                  </p>
                  {notStarted ? (
                    <p style={{ fontFamily: pressFont, fontSize: '10px', color: tone }}>Not started</p>
                  ) : (
                    <p style={{ fontFamily: pressFont, fontSize: '18px', color: tone }}>{pct}%</p>
                  )}
                  <p style={{ fontFamily: pressFont, fontSize: '6px', color: '#6b7280', marginTop: 6, lineHeight: 1.6 }}>
                    {notStarted
                      ? `${s.count} standard${s.count !== 1 ? 's' : ''} · no attempts yet`
                      : `${s.onTrack}/${s.total} on track · ${s.count} standard${s.count !== 1 ? 's' : ''}`}
                  </p>
                </div>
              )
            })}
          </div>

          {/* Focus here */}
          {focusStandards.length > 0 && (
            <div className="mb-6 p-4 rounded-lg" style={{ background: '#0f0f23', border: '1px solid #4338ca' }}>
              <p style={{ fontFamily: pressFont, fontSize: '9px', color: '#fbbf24', marginBottom: 4 }}>
                🎯 Focus here
              </p>
              <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#6b7280', marginBottom: 12 }}>
                Standards where the class is furthest from proficient
                {Object.keys(mstepById).length > 0 && ', weighted by historical M-STEP difficulty'}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {focusStandards.map(s => (
                  <div key={s.standard_id} className="p-3 rounded-lg flex items-center gap-3"
                       style={{ background: '#1a1733', border: '1px solid #312e81' }}>
                    <div style={{
                      flexShrink: 0, width: 44, height: 44, borderRadius: 6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#0a0a1a',
                      fontFamily: pressFont, fontSize: '11px',
                      color: s.rate >= 0.5 ? '#f59e0b' : '#ef4444',
                    }}>
                      {Math.round(s.rate * 100)}%
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: pressFont, fontSize: '7px', color: '#a78bfa' }}>{s.standard_id}</p>
                      <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#e0e7ff', marginTop: 3 }}>
                        {s.boss_name}
                      </p>
                      <p style={{ fontFamily: 'system-ui', fontSize: '11px', color: '#6b7280', marginTop: 2 }}>
                        {s.strand}{s.mstep != null && ` · M-STEP avg ${s.mstep}%`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legend + grid controls */}
          <div className="flex gap-4 mb-4 flex-wrap items-center">
            {Object.entries(MASTERY_LABELS).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1">
                <div style={{ width: 12, height: 12, borderRadius: 2, background: MASTERY_COLORS[k] }} />
                <span style={{ fontFamily: pressFont, fontSize: '6px', color: '#6b7280' }}>
                  {v} {k.replace('_', ' ')}
                </span>
              </div>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <label style={{ fontFamily: pressFont, fontSize: '6px', color: '#818cf8' }}>STRAND</label>
              <select value={strandFilter} onChange={e => setStrandFilter(e.target.value)}
                style={{ ...fieldStyle, fontSize: '12px', padding: '4px 8px' }}>
                <option value="">All</option>
                {strandOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Mastery matrix */}
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ borderCollapse: 'collapse', minWidth: 800 }}>
              <thead>
                <tr>
                  <SortTh label="Student" className="px-3 py-2 text-left sticky left-0 z-10"
                      active={gridSort.key === 'name'} dir={gridSort.dir}
                      onClick={() => gridSort.toggle('name', 'asc')}
                      style={{ background: '#0f0f23', fontFamily: pressFont, fontSize: '7px', color: '#818cf8', minWidth: 140 }} />
                  {visibleStds.map((s: any) => {
                    const key = `std:${s.standard_id}`
                    return (
                      <SortTh key={s.standard_id} label={s.standard_id} className="px-1 py-2"
                          title={`${s.standard_id} · ${s.boss_name} — click to sort students by this standard`}
                          active={gridSort.key === key} dir={gridSort.dir}
                          // Low→high first: the kids who need help on this standard rise to the top.
                          onClick={() => gridSort.toggle(key, 'asc')}
                          style={{ background: '#0f0f23', fontFamily: pressFont, fontSize: '5px',
                                   color: '#818cf8', maxWidth: 40, minWidth: 28 }}
                          buttonStyle={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }} />
                    )
                  })}
                  <SortTh label="⭐ Mstd" className="px-3 py-2"
                      active={gridSort.key === 'mastered'} dir={gridSort.dir}
                      onClick={() => gridSort.toggle('mastered', 'desc')}
                      style={{ background: '#0f0f23', fontFamily: pressFont, fontSize: '6px', color: '#fbbf24' }} />
                  <SortTh label="✅ Prof" className="px-3 py-2"
                      active={gridSort.key === 'proficient'} dir={gridSort.dir}
                      onClick={() => gridSort.toggle('proficient', 'desc')}
                      style={{ background: '#0f0f23', fontFamily: pressFont, fontSize: '6px', color: '#6366f1' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ stu, mastered, proficient }, i: number) => (
                  <tr key={stu.student_id} style={{ background: i % 2 === 0 ? 'transparent' : '#0a0a1a' }}>
                    <td className="px-3 py-2 sticky left-0"
                        onClick={() => setSelectedStudent(stu)}
                        title="Click for student detail"
                        style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#e0e7ff',
                                 background: i % 2 === 0 ? '#0a0a1a' : '#050510', minWidth: 140,
                                 borderRight: '1px solid #1e1b4b', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                      {stu.display_name}
                      <span style={{ fontFamily: pressFont, fontSize: '6px', color: '#fbbf24', marginLeft: 8 }}>
                        LV{stu.level}
                      </span>
                    </td>
                    {visibleStds.map((s: any) => {
                      const m = stu.standards[s.standard_id]
                      const status = m?.mastery || 'not_started'
                      return (
                        <td key={s.standard_id} className="px-1 py-2 text-center"
                            title={`${stu.display_name} · ${s.standard_id} · ${status}${m?.pct !== undefined ? ` (${m.pct}%)` : ''}`}>
                          <div style={{
                            width: 20, height: 20, borderRadius: 3,
                            background: MASTERY_COLORS[status],
                            margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, cursor: 'default',
                          }}>
                            {MASTERY_LABELS[status]}
                          </div>
                        </td>
                      )
                    })}
                    <td className="px-3 py-2 text-center"
                        style={{ fontFamily: pressFont, fontSize: '8px', color: '#10b981' }}>
                      {mastered}
                    </td>
                    <td className="px-3 py-2 text-center"
                        style={{ fontFamily: pressFont, fontSize: '8px', color: '#6366f1' }}>
                      {proficient}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mastery empty state */}
      {mode === 'mastery' && reportData && studs.length === 0 && (
        <p className="text-center py-10" style={{ fontFamily: pressFont, fontSize: '9px', color: '#6b7280' }}>
          No students in this class yet. Add a roster, then generate the report.
        </p>
      )}

      {/* Student drill-down modal */}
      {selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: 'rgba(5,5,20,0.85)' }}
             onClick={() => setSelectedStudent(null)}
             role="dialog" aria-modal="true">
          <div onClick={e => e.stopPropagation()}
               style={{ background: '#0f0f23', border: '2px solid #312e81', borderRadius: 12,
                        padding: '22px 24px', maxWidth: 520, width: '100%', maxHeight: '86vh', overflowY: 'auto' }}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p style={{ fontFamily: pressFont, fontSize: '11px', color: '#e0e7ff' }}>{selectedStudent.display_name}</p>
                <p style={{ fontFamily: pressFont, fontSize: '7px', color: '#fbbf24', marginTop: 6 }}>
                  Level {selectedStudent.level}
                </p>
              </div>
              <button onClick={() => setSelectedStudent(null)}
                style={{ fontFamily: pressFont, fontSize: '8px', color: '#818cf8',
                         background: '#1e1b4b', border: '1px solid #4338ca', borderRadius: 4,
                         padding: '5px 10px', cursor: 'pointer' }}>
                CLOSE
              </button>
            </div>
            <div className="space-y-2">
              {stds.map((s: any) => {
                const m = selectedStudent.standards[s.standard_id]
                const status = m?.mastery || 'not_started'
                return (
                  <div key={s.standard_id} className="flex items-center gap-3 p-2 rounded"
                       style={{ background: '#0a0a1a' }}>
                    <div style={{ flexShrink: 0, width: 16, height: 16, borderRadius: 3, background: MASTERY_COLORS[status] }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: pressFont, fontSize: '6px', color: '#a78bfa' }}>{s.standard_id}</p>
                      <p style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#94a3b8' }}>{s.boss_name}</p>
                    </div>
                    <span style={{ fontFamily: 'system-ui', fontSize: '12px', color: '#e0e7ff' }}>
                      {MASTERY_LABELS[status]} {MASTERY_TEXT[status]}{m?.pct !== undefined ? ` · ${m.pct}%` : ''}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── GROWTH MODE ───────────────────────────────────────────────────── */}
      {mode === 'growth' && growthData && (
        <div className="space-y-3">
          <p style={{ fontFamily: pressFont, fontSize: '7px', color: '#6b7280', marginBottom: 12 }}>
            Green = improvement · Red = decline · Gray = no data yet
          </p>
          {growthRows.length > 0 && (
            <table style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ fontFamily: pressFont, fontSize: '6px', color: '#818cf8' }}>
                  <th style={{ padding: '4px 10px 4px 0', textAlign: 'left', fontWeight: 'normal' }}>SORT BY</th>
                  {([
                    ['standard', 'STANDARD', 'asc'],
                    ['pre',      'PRE',      'desc'],
                    ['post',     'POST',     'desc'],
                    ['change',   'CHANGE',   'desc'],
                  ] as const).map(([k, label, first]) => (
                    <SortTh key={k} label={label} active={growthSort.key === k} dir={growthSort.dir}
                      onClick={() => growthSort.toggle(k, first)}
                      style={{ padding: '4px 10px', fontWeight: 'normal' }} />
                  ))}
                </tr>
              </thead>
            </table>
          )}
          {growthRows.map((g: any) => {
            const delta = g.delta
            const deltaColor = delta === null ? '#6b7280' : delta > 0 ? '#34d399' : delta < 0 ? '#ef4444' : '#6b7280'
            return (
              <div key={g.standard_id} className="p-4 rounded-lg flex gap-6 items-center"
                   style={{ background: '#0f0f23', border: '1px solid #1e1b4b' }}>
                <div style={{ minWidth: 100 }}>
                  <p style={{ fontFamily: pressFont, fontSize: '7px', color: '#a78bfa' }}>{g.standard_id}</p>
                  <p style={{ fontFamily: 'system-ui', fontSize: '11px', color: '#6b7280', marginTop: 2 }}>{g.boss_name}</p>
                </div>
                <div className="flex gap-8 items-center flex-1">
                  <div className="text-center">
                    <p style={{ fontFamily: pressFont, fontSize: '6px', color: '#6b7280' }}>PRE</p>
                    <p style={{ fontFamily: pressFont, fontSize: '14px', color: g.pre ? '#e0e7ff' : '#374151' }}>
                      {g.pre ? `${g.pre.avg_pct}%` : '—'}
                    </p>
                    {g.pre && <p style={{ fontFamily: pressFont, fontSize: '6px', color: '#6b7280' }}>{g.pre.count} students</p>}
                  </div>
                  <div className="text-2xl">→</div>
                  <div className="text-center">
                    <p style={{ fontFamily: pressFont, fontSize: '6px', color: '#6b7280' }}>POST</p>
                    <p style={{ fontFamily: pressFont, fontSize: '14px', color: g.post ? '#e0e7ff' : '#374151' }}>
                      {g.post ? `${g.post.avg_pct}%` : '—'}
                    </p>
                    {g.post && <p style={{ fontFamily: pressFont, fontSize: '6px', color: '#6b7280' }}>{g.post.count} students</p>}
                  </div>
                  <div className="text-center ml-4">
                    <p style={{ fontFamily: pressFont, fontSize: '6px', color: '#6b7280' }}>CHANGE</p>
                    <p style={{ fontFamily: pressFont, fontSize: '16px', color: deltaColor }}>
                      {delta !== null ? `${delta > 0 ? '+' : ''}${delta}%` : '—'}
                    </p>
                  </div>
                </div>
                {g.pre && g.post && (
                  <div style={{ minWidth: 160 }}>
                    <div className="h-3 rounded-full mb-1 relative overflow-hidden" style={{ background: '#1e1b4b' }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${g.pre.avg_pct}%`, background: '#4338ca', borderRadius: 4, opacity: 0.6 }} />
                    </div>
                    <div className="h-3 rounded-full relative overflow-hidden" style={{ background: '#1e1b4b' }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${g.post.avg_pct}%`,
                        background: delta !== null && delta > 0 ? '#34d399' : '#ef4444', borderRadius: 4 }} />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
          {growthRows.length === 0 && (
            <p className="text-center py-10" style={{ fontFamily: pressFont, fontSize: '9px', color: '#6b7280' }}>
              No growth data for these sessions yet.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
